import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PageShell, Icon, Toast, EmptyState, Skeleton } from '../../components';
import { PLAYERS } from '../../theme/tokens';
import { useAppSelector } from '@/store';
import { useToast } from '../../hooks/useToast';
import type { Player } from '../../mock-data/types';
import { useStuckOnPin } from '../home/components/SectionHead/useStuckOnPin';
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { assetKindOpens, useAssetOverlay } from '../creatives/components/AssetComposerOverlay';
import type { TrackingLinkKind } from '../creatives/tracking-links-api';
import { PLAYERS_FROM_PARAMS } from '../creatives/link-attribution';
import { useBrandLinks } from '../creatives/useBrandLinks';
import { isAffiliateCampaignNo } from '../creatives/affiliate-campaign';
import { useAffiliateDirectory } from '../website-analytics/useAffiliateDirectory';
import { usePlayersRoster } from './usePlayersRoster';
import { useCommissionPlans } from './useCommissionPlans';
import { commissionViewOf, type CommissionView } from './commission-estimate';
import {
  campaignChipLabel,
  campaignSetsOf,
  cameFromOf,
  landingChipLabel,
  type AffiliateNames,
} from './attribution';
import {
  ANY,
  DEFAULT_FILTERS,
  DEFAULT_SORT,
  SORT_KEYS,
  activeFilterCount,
  filterByCampaignSets,
  filterPlayers,
  isSortKey,
  sortPlayers,
  sourceOptions,
  toActivityFilter,
  toRegisteredFilter,
  toSeenFilter,
  type PlayerFilters,
  type SortKey,
} from './roster-view';
import { PlayersTable, PlayersFilters, PlayersPagination, PlayerDetailSheet } from './components';
import {
  Head,
  HeadRow,
  HeadTitle,
  StickySentinel,
  Controls,
  SortBox,
  SortValue,
  SortSelect,
  SortChevron,
  FilterToggle,
  FilterToggleLabel,
  FilterBadge,
  Container,
  PagerFooter,
  RosterNote,
  FilterTray,
  FilterTrayInner,
  FromBar,
  FromFilterChip,
  FromFilterLabel,
  FromFilterRemove,
  FromNote,
} from './players.styles';

export type PlayersProps = {
  styleText?: string;
  className?: string;
};

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50];
const DEFAULT_ROWS_PER_PAGE = 20;
/** Ties the Filters button to the tray it expands (`aria-controls`). */
const FILTER_TRAY_ID = 'players-filter-tray';

/**
 * Players page — the casino's full player roster as a column of cards (Avatar ·
 * Email · Deposits · Withdrawals · Net cash · Bet amount · Win amount · GGR · NGR).
 * The email copies to the clipboard with a toast; each card opens a detail sheet
 * (centred modal on desktop, bottom sheet on mobile) with the fuller breakdown
 * (registration, source, activity, cash, revenue and the affiliate's estimated
 * commission).
 *
 * The roster merges the Stats API's two player data sets — the `players`
 * registration list (every account) enriched with `daily_player_activity` (only the
 * players who transacted); see {@link usePlayersRoster}. Because most of the roster
 * sits at the top of the funnel, the page leads with **Newest first** and offers a
 * filter tray over the funnel stage, registration cohort, last-seen recency and
 * acquisition source.
 *
 * The whole view lives in the URL — `?sort=`, `?page=` and one param per filter — so
 * a link restores the exact view on refresh / share / back-button. Default values
 * carry no param, keeping the canonical URL clean. Neither data set supports
 * server-side search or cross-set filtering, so both are paged into memory (again on
 * every open of this page) and the filtering / sorting / pagination all run client-side
 * over the merge.
 */
const Players = ({ styleText = '', className = '' }: PlayersProps): React.ReactElement => {
  const { t } = useTranslation();
  // Sort option labels, translated reactively at the render site (the keys stay
  // stable English ids used for URL/state comparison).
  const sortLabels: Record<SortKey, string> = {
    'registered-desc': t('Newest first'),
    'registered-asc': t('Oldest first'),
    'lastseen-desc': t('Recently seen'),
    'wagered-desc': t('Most wagered'),
    'ggr-desc': t('Most profitable'),
    'deposits-desc': t('Most deposited'),
    'wins-desc': t('Most won'),
    'withdrawals-desc': t('Most withdrawn'),
    'email-asc': t('Email A–Z'),
    'email-desc': t('Email Z–A'),
  };
  const { toast, show } = useToast();
  // Pins the header under the layout top bar and morphs it into a floating pill
  // once the roster scrolls beneath it (same behaviour as Home / Stats).
  const { sentinelRef, stuck } = useStuckOnPin(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [selected, setSelected] = React.useState<Player | null>(null);
  const [trayOpen, setTrayOpen] = React.useState(false);

  // View state derived from the URL. Each falls back to its default (which the
  // writers below then drop from the URL, so the canonical view is param-free).
  const sortParam = searchParams.get('sort');
  const sort: SortKey = isSortKey(sortParam) ? sortParam : DEFAULT_SORT;
  const pageParam = Number(searchParams.get('page'));
  const requestedPage = Number.isInteger(pageParam) && pageParam >= 1 ? pageParam : 1;

  const activityParam = searchParams.get('activity');
  const registeredParam = searchParams.get('registered');
  const seenParam = searchParams.get('seen');
  const sourceParam = searchParams.get('source');
  const filters: PlayerFilters = React.useMemo(
    () => ({
      activity: toActivityFilter(activityParam),
      registered: toRegisteredFilter(registeredParam),
      seen: toSeenFilter(seenParam),
      // The source set is per-brand data, so any non-empty value is carried through
      // and simply matches nothing if the brand has no players from it.
      source: sourceParam && sourceParam !== '' ? sourceParam : ANY,
    }),
    [activityParam, registeredParam, seenParam, sourceParam],
  );

  // One writer for both params. `replace` for view config (sort / filters — they
  // shouldn't spam history); `push` for page navigation (so Back steps through pages).
  const setParams = React.useCallback(
    (mutate: (params: URLSearchParams) => void, replace: boolean): void => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace },
      );
    },
    [setSearchParams],
  );

  // Changing the sort / filters / page size always returns to page 1 (drop `page`).
  const onSortChange = React.useCallback(
    (value: SortKey): void => {
      setParams((p) => {
        if (value !== DEFAULT_SORT) p.set('sort', value);
        else p.delete('sort');
        p.delete('page');
      }, true);
    },
    [setParams],
  );
  const onFiltersChange = React.useCallback(
    (next: PlayerFilters): void => {
      setParams((p) => {
        const write = (key: string, value: string, isDefault: boolean): void => {
          if (isDefault) p.delete(key);
          else p.set(key, value);
        };
        write('activity', next.activity, next.activity === DEFAULT_FILTERS.activity);
        write('registered', next.registered, next.registered === DEFAULT_FILTERS.registered);
        write('seen', next.seen, next.seen === DEFAULT_FILTERS.seen);
        write('source', next.source, next.source === DEFAULT_FILTERS.source);
        p.delete('page');
      }, true);
    },
    [setParams],
  );
  const onRowsPerPageChange = React.useCallback(
    (rows: number): void => {
      setRowsPerPage(rows);
      setParams((p) => p.delete('page'), true);
    },
    [setParams],
  );
  const goToPage = React.useCallback(
    (next: number): void => {
      setParams((p) => {
        if (next <= 1) p.delete('page');
        else p.set('page', String(next));
      }, false);
    },
    [setParams],
  );

  // Active brand — the lagged `displayBrand` (mirrors Home / Stats); the roster
  // re-fetches when the owner switches brands.
  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  // The merged roster (registrations + activity). `{ data, loading }` matches the
  // old mock hook; `error` / `truncated` drive the extra states below.
  const roster = usePlayersRoster(displayBrand?.id, { refreshOnMount: true });
  const players = roster.data;

  // Where each player came from: their stored `campaign` resolved against the brand's tracking links (read once here,
  // handed to the cards and the sheet). Until the first read lands an unknown value may still be a link.
  const brandLinks = useBrandLinks();
  const linksPending = brandLinks.loading && brandLinks.links.length === 0;
  const linksFailed = brandLinks.error !== null && brandLinks.links.length === 0;
  // An asset (or landing page) deep link only where that kind's pages are on; elsewhere its name shows as plain text.
  const gate = useFeatureGate();
  const opens = React.useCallback((kind: TrackingLinkKind) => assetKindOpens(kind, gate.isEnabled), [gate]);
  // Which affiliate brought an affiliate-link player: the brand's affiliates and campaigns from the affiliates platform,
  // read only when the roster has such a player (a brand without affiliates never asks). Until it answers, or when it
  // cannot, the card keeps naming the campaign alone.
  const hasAffiliatePlayers = React.useMemo(() => players.some((p) => isAffiliateCampaignNo(p.campaign)), [players]);
  const directory = useAffiliateDirectory(displayBrand?.id, hasAffiliatePlayers);
  // Affiliate number (what a player stores) to the platform's affiliate id: deep links to the affiliate, and the key
  // their commission plan is read by.
  const idByNo = React.useMemo(
    () => new Map(directory.affiliates.flatMap((entry) => (entry.id ? [[entry.value, entry.id] as const] : []))),
    [directory.affiliates],
  );
  const affiliateNames = React.useMemo<AffiliateNames>(
    () => ({
      affiliates: new Map(directory.affiliates.map((entry) => [entry.value, entry.name])),
      campaigns: new Map(directory.campaigns.map((entry) => [entry.value, entry.name])),
      affiliateIds: idByNo,
    }),
    [directory.affiliates, directory.campaigns, idByNo],
  );
  const cameFrom = React.useCallback(
    (player: Player) => cameFromOf(player, brandLinks.byCampaign, linksPending, t, opens, affiliateNames),
    [brandLinks.byCampaign, linksPending, t, opens, affiliateNames],
  );

  // What each affiliate earns for their players, ESTIMATED from the player's figures and the affiliate's plan (the
  // affiliates platform reports commission per affiliate and month only; see commission-estimate.ts). Only the sheet
  // shows it, so only the opened player's affiliate plan is read (once the affiliates list has named them), not one
  // read per affiliate on the roster.
  const selectedAffiliateId =
    selected?.affiliate && isAffiliateCampaignNo(selected.campaign) ? idByNo.get(selected.affiliate) : undefined;
  const planAffiliateIds = React.useMemo(() => (selectedAffiliateId ? [selectedAffiliateId] : []), [selectedAffiliateId]);
  const commissionPlans = useCommissionPlans(displayBrand?.id, directory.brandId, planAffiliateIds);
  const commissionOf = React.useCallback(
    (player: Player): CommissionView =>
      commissionViewOf(player, {
        directoryPending: directory.status === 'loading' || (hasAffiliatePlayers && directory.status === 'idle'),
        idByNo,
        plans: commissionPlans.plans,
        plansPending: commissionPlans.pending,
      }),
    [directory.status, hasAffiliatePlayers, idByNo, commissionPlans],
  );
  const brandName = displayBrand?.casino_data?.details?.brand;

  // The "came from" filter (`?campaign=` one link's token or an affiliates-platform number, `?landing_page=` every
  // link landing on that page), read from the URL like the rest of the view and AND-ed with the tray's filters. It runs
  // over the loaded roster: the registration list is read whole (server-side filtering would mean another
  // paged read per link), so a capped roster says so in its note below.
  const campaignParam = (searchParams.get(PLAYERS_FROM_PARAMS.campaign) ?? '').trim();
  const landingParam = (searchParams.get(PLAYERS_FROM_PARAMS.landingPage) ?? '').trim();
  const fromActive = campaignParam !== '' || landingParam !== '';
  const campaignSets = React.useMemo(
    () => campaignSetsOf(campaignParam, landingParam, brandLinks.links, linksFailed),
    [campaignParam, landingParam, brandLinks.links, linksFailed],
  );
  // A landing page filter needs the brand's links to know its campaigns: the list stays on its skeleton until they land
  // (rather than flashing "no players match").
  const listLoading = roster.loading || (landingParam !== '' && linksPending);
  const clearFrom = React.useCallback(
    (key: string): void => {
      setParams((p) => {
        p.delete(key);
        p.delete('page');
      }, true);
    },
    [setParams],
  );
  const campaignLabel = campaignParam ? campaignChipLabel(campaignParam, brandLinks.byCampaign, linksPending, t) : undefined;
  const landingLabel = landingParam ? landingChipLabel(brandLinks.links, landingParam, linksPending, t) : undefined;

  // A "came from" link opens in the app: an asset (or its landing page) in its composer over this page (`?asset=`, see
  // useAssetOverlay), the Affiliates page by navigating. From the sheet, the sheet closes first, then the asset opens.
  // Closing it re-reads the brand's links, so a rename or a moved link shows at once (as on Home).
  const assetOverlay = useAssetOverlay({ onNotify: show, onClosed: brandLinks.reload });
  const { openPath: openAssetPath } = assetOverlay;
  const openPath = React.useCallback((path: string): void => openAssetPath(path), [openAssetPath]);
  const openPathFromSheet = React.useCallback(
    (path: string): void => {
      setSelected(null);
      openAssetPath(path);
    },
    [openAssetPath],
  );

  const copyEmail = React.useCallback(
    (email: string): void => {
      void navigator.clipboard
        ?.writeText(email)
        .then(() => show(t('Email copied to clipboard')))
        .catch(() => show(t("Couldn't copy to clipboard")));
    },
    [show, t],
  );

  // The filter tray's options are derived from the roster, so a brand is never
  // offered a source it has no players from.
  const sources = React.useMemo(() => sourceOptions(players), [players]);

  // The relative filters ("registered in the last 7 days") need a clock reading.
  // Taking it INSIDE the memo pins it to the roster/filter inputs: the list can't
  // silently reshuffle under the reader as minutes pass, only when something they
  // changed says it should.
  const visible = React.useMemo(
    () => sortPlayers(filterPlayers(filterByCampaignSets(players, campaignSets), filters, Date.now()), sort),
    [players, campaignSets, filters, sort],
  );

  const filterCount = activeFilterCount(filters);
  // The tray never opens over a skeleton or an error state — there is nothing to
  // filter yet, and its source options would be empty.
  const trayIsOpen = trayOpen && !roster.loading && !roster.error;
  const total = visible.length;
  const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));
  const safePage = Math.min(requestedPage, pageCount);
  const start = (safePage - 1) * rowsPerPage;
  const pageItems = visible.slice(start, start + rowsPerPage);

  // Keep the URL honest: tidy a stale / out-of-range / non-canonical `page` param
  // (e.g. a shared `?page=9` link after the roster shrank, or `?page=1`).
  React.useEffect(() => {
    // Not while the roster is still loading or failed: `visible` is empty in both
    // states, so EVERY page reads as out of range and a shared `?page=4` link would
    // be rewritten to page 1 before its rows arrived (or before a retry could load
    // them at all).
    if (listLoading || roster.error) return;
    const canonical = safePage <= 1 ? '' : String(safePage);
    if ((searchParams.get('page') ?? '') !== canonical) {
      setParams((p) => {
        if (canonical === '') p.delete('page');
        else p.set('page', canonical);
      }, true);
    }
  }, [listLoading, roster.error, safePage, searchParams, setParams]);

  return (
    <PageShell styleText={styleText} className={className}>
      <StickySentinel ref={sentinelRef} aria-hidden="true" />
      <Head $sticky $stuck={stuck} data-stuck={stuck}>
        <HeadRow>
          {/* The count reflects the CURRENT filters, so it always matches the list. */}
          <HeadTitle title={t('Players')} count={listLoading ? '' : String(total)} />
          <Controls>
            <FilterToggle
              type="button"
              aria-expanded={trayIsOpen}
              aria-controls={FILTER_TRAY_ID}
              aria-label={t('Filter players')}
              $active={trayOpen || filterCount > 0}
              onClick={() => setTrayOpen((open) => !open)}
            >
              <Icon name="filter" size={15} color={PLAYERS.searchIcon} />
              <FilterToggleLabel>{t('Filters')}</FilterToggleLabel>
              {filterCount > 0 ? <FilterBadge>{filterCount}</FilterBadge> : null}
            </FilterToggle>

            <SortBox>
              {/* The `filter` glyph now belongs to the Filters button, so sort leads
                  with the ranking glyph (it is the only face left on mobile, where the
                  label and chevron collapse away). */}
              <Icon name="trending-up" size={15} color={PLAYERS.searchIcon} />
              <SortValue>{sortLabels[sort]}</SortValue>
              <SortChevron>
                <Icon name="chevron-expand" size={14} />
              </SortChevron>
              <SortSelect
                value={sort}
                aria-label={t('Sort players')}
                onChange={(event) => onSortChange(event.target.value as SortKey)}
              >
                {SORT_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {sortLabels[key]}
                  </option>
                ))}
              </SortSelect>
            </SortBox>
          </Controls>
        </HeadRow>

        {/* The tray lives INSIDE the sticky block, so the filters pin with the
            header and stay reachable however far the roster has scrolled. It stays
            mounted while closed (collapsed to zero height) so the open/close eases
            both ways; `hidden` keeps the collapsed fields out of the tab order. */}
        <FilterTray id={FILTER_TRAY_ID} $open={trayIsOpen} aria-hidden={!trayIsOpen}>
          {/* `visibility: hidden` (not just height 0) is what actually drops the
              collapsed fields out of the tab order and out of hit-testing. */}
          <FilterTrayInner $open={trayIsOpen}>
            <PlayersFilters
              filters={filters}
              onChange={onFiltersChange}
              sources={sources}
              affiliateNames={affiliateNames.affiliates}
              shown={total}
              total={players.length}
            />
          </FilterTrayInner>
        </FilterTray>
      </Head>

      <Container>
        {fromActive ? (
          <FromBar>
            {campaignParam ? (
              <FromFilterChip>
                {campaignLabel === undefined ? (
                  <Skeleton width={120} height={12} radius={5} />
                ) : (
                  <FromFilterLabel raw text={campaignLabel} title={campaignLabel} tag="span" />
                )}
                <FromFilterRemove
                  type="button"
                  aria-label={t('Remove filter')}
                  onClick={() => clearFrom(PLAYERS_FROM_PARAMS.campaign)}
                >
                  <Icon name="close" size={12} />
                </FromFilterRemove>
              </FromFilterChip>
            ) : null}
            {landingParam ? (
              <FromFilterChip>
                {landingLabel === undefined ? (
                  <Skeleton width={120} height={12} radius={5} />
                ) : (
                  <FromFilterLabel raw text={landingLabel} title={landingLabel} tag="span" />
                )}
                <FromFilterRemove
                  type="button"
                  aria-label={t('Remove filter')}
                  onClick={() => clearFrom(PLAYERS_FROM_PARAMS.landingPage)}
                >
                  <Icon name="close" size={12} />
                </FromFilterRemove>
              </FromFilterChip>
            ) : null}
            {landingParam && linksFailed ? (
              <FromNote
                text="We couldn't load your tracking links, so this list isn't narrowed to the landing page."
                tag="span"
              />
            ) : null}
          </FromBar>
        ) : null}

        {roster.error ? (
          <EmptyState text={t("We couldn't load your players. Please try again.")} />
        ) : (
          <>
            <PlayersTable
              players={pageItems}
              loading={listLoading}
              onCopyEmail={copyEmail}
              onSelect={setSelected}
              emptyText={filterCount > 0 || fromActive ? t('No players match these filters.') : t('No players yet.')}
              cameFrom={cameFrom}
              onOpenPath={openPath}
            />

            {!listLoading && total > 0 ? (
              <PagerFooter>
                <PlayersPagination
                  page={safePage}
                  pageCount={pageCount}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                  rangeStart={start + 1}
                  rangeEnd={start + pageItems.length}
                  total={total}
                  onPageChange={goToPage}
                  onRowsPerPageChange={onRowsPerPageChange}
                />
              </PagerFooter>
            ) : null}

            {/* Say which half of the roster is missing. A figure this page filled in
                itself must never read as a measured one, so a failed activity fetch
                is stated outright rather than left to look like a brand nobody has
                played yet. */}
            {!roster.loading && roster.activityFailed ? (
              <RosterNote
                text="We couldn't load player activity, so every deposit, bet and win below reads as zero."
                tag="span"
              />
            ) : null}

            {!roster.loading && roster.registrationsFailed ? (
              <RosterNote
                text="We couldn't load your full player list, so only players with activity are shown."
                tag="span"
              />
            ) : null}

            {!roster.loading && roster.truncatedRegistrations ? (
              <RosterNote
                text="Showing your {{count}} most recent players. Older registrations aren't loaded."
                placeholders={{ count: roster.registrationCount.toLocaleString('en-US') }}
                tag="span"
              />
            ) : null}

            {!roster.loading && roster.truncatedActivity ? (
              <RosterNote
                text="Activity is loaded for your top {{count}} players by amount wagered. Players beyond that show as having none yet."
                placeholders={{ count: roster.activityCount.toLocaleString('en-US') }}
                tag="span"
              />
            ) : null}
          </>
        )}
      </Container>

      {selected ? (
        <PlayerDetailSheet
          player={selected}
          onClose={() => setSelected(null)}
          onCopyEmail={copyEmail}
          cameFrom={cameFrom(selected)}
          commission={commissionOf(selected)}
          brandName={brandName}
          onOpenPath={openPathFromSheet}
        />
      ) : null}

      {assetOverlay.node}

      <Toast toast={toast} />
    </PageShell>
  );
};

export default Players;
